import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Check, Trash2, LogOut, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "kos_admin_token";

const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });

const Stars = ({ n }) => (
  <div className="flex text-brand-gold">
    {[...Array(5)].map((_, k) => (
      <Star key={k} size={15} fill={k < n ? "currentColor" : "none"} className={k < n ? "" : "text-slate-300"} />
    ))}
  </div>
);

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.user);
      toast.success("Connexion réussie");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connexion impossible");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-navy px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-lg bg-white p-8 space-y-5">
        <div className="flex items-center gap-2 text-brand-navy">
          <ShieldCheck className="text-brand-gold" />
          <span className="font-display font-bold text-xl">Modération — Admin</span>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@…" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Mot de passe</Label>
          <Input id="password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy px-6 py-3 text-sm font-bold uppercase tracking-wide text-white hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : null} Se connecter
        </button>
      </form>
    </div>
  );
};

const Panel = ({ user, onLogout }) => {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews?status=${status}`, authHeader());
      setItems(data || []);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { load(tab); }, [tab, load]);

  const approve = async (id) => {
    try {
      await axios.patch(`${API}/admin/reviews/${id}/approve`, {}, authHeader());
      setItems((it) => it.filter((r) => r.id !== id));
      toast.success("Avis approuvé et publié");
    } catch { toast.error("Action impossible"); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, authHeader());
      setItems((it) => it.filter((r) => r.id !== id));
      toast.success("Avis supprimé");
    } catch { toast.error("Action impossible"); }
  };

  const tabs = [
    { k: "pending", label: "En attente" },
    { k: "approved", label: "Publiés" },
    { k: "all", label: "Tous" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-brand-navy text-white">
        <div className="mx-auto max-w-5xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-brand-gold" size={20} />
            <span className="font-display font-bold">Modération des avis</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 py-8">
        <div className="flex gap-2 mb-6">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`admin-tab-${t.k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${tab === t.k ? "bg-brand-navy text-white" : "bg-white text-brand-navy border border-black/10"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-brand-navy"><Loader2 className="animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="text-slate-500 py-16 text-center" data-testid="admin-empty">Aucun avis dans cette catégorie.</p>
        ) : (
          <div className="space-y-3" data-testid="admin-reviews-list">
            {items.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="rounded-lg bg-white border border-black/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Stars n={r.rating} />
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.status === "approved" ? "Publié" : "En attente"}
                    </span>
                  </div>
                  <p className="text-sm text-slate-700">"{r.text}"</p>
                  {r.media && r.media.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3" data-testid={`admin-media-${r.id}`}>
                      {r.media.map((m, k) => (
                        m.kind === "video" ? (
                          <video key={k} src={`${BACKEND_URL}${m.url}`} controls playsInline className="h-16 w-16 rounded-md object-cover bg-black/10 border border-black/10" />
                        ) : (
                          <a key={k} href={`${BACKEND_URL}${m.url}`} target="_blank" rel="noopener noreferrer">
                            <img src={`${BACKEND_URL}${m.url}`} alt="Colis" className="h-16 w-16 rounded-md object-cover border border-black/10" />
                          </a>
                        )
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-2">{r.name}{r.company ? ` — ${r.company}` : ""}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {r.status !== "approved" && (
                    <button onClick={() => approve(r.id)} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-green-600 text-white px-4 py-2 text-xs font-bold hover:bg-green-700 transition-colors">
                      <Check size={14} /> Approuver
                    </button>
                  )}
                  <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-red-50 text-red-600 border border-red-200 px-4 py-2 text-xs font-bold hover:bg-red-100 transition-colors">
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/auth/me`, authHeader())
      .then(({ data }) => setUser(data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-navy text-white"><Loader2 className="animate-spin" /></div>;
  }
  return user ? <Panel user={user} onLogout={logout} /> : <Login onLogin={setUser} />;
}
